import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'

const ChangeUser = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('Trocando usuário...')

  useEffect(() => {
    const changeAccount = async () => {
      const accountId = searchParams.get('accountId')

      if (!accountId) {
        setStatus('error')
        setMessage('ID da conta não fornecido')
        setTimeout(() => navigate('/'), 2000)
        return
      }

      if (!user) {
        setStatus('error')
        setMessage('Usuário não autenticado')
        setTimeout(() => navigate('/login'), 2000)
        return
      }

      try {
        console.log('🔄 [ChangeUser] Iniciando troca de conta:', {
          from: user.account_id,
          to: accountId
        })

        if (!supabase) {
          throw new Error('Supabase client não disponível')
        }

        // Verificar se o usuário é agency_user
        const { data: agencyUser, error: agencyUserError } = await supabase
          .from('agency_users')
          .select('agency_id')
          .eq('auth_user_id', user.auth_user_id)
          .maybeSingle()

        if (agencyUserError) {
          throw new Error('Erro ao verificar usuário da agência')
        }

        if (!agencyUser) {
          throw new Error('Apenas usuários de agência podem trocar de conta')
        }

        // Buscar a nova conta
        const { data: newAccount, error: accountError } = await supabase
          .from('accounts')
          .select('*')
          .eq('id', accountId)
          .maybeSingle()

        if (accountError || !newAccount) {
          throw new Error('Conta não encontrada')
        }

        if (newAccount.is_active === false) {
          throw new Error('Esta conta está desativada')
        }

        // Validar se a conta pertence à agência do usuário
        if (newAccount.agency_id !== agencyUser.agency_id) {
          throw new Error('Você não tem permissão para acessar esta conta')
        }

        console.log('✅ [ChangeUser] Nova conta validada:', {
          id: newAccount.id,
          nome: newAccount.nome,
          agency_id: newAccount.agency_id
        })

        // Atualizar sessão RLS no banco
        setMessage('Atualizando permissões de acesso...')

        const { error: sessionError } = await supabase.rpc('update_agency_user_session', {
          p_account_id: newAccount.id
        })

        if (sessionError) {
          console.error('❌ [ChangeUser] Erro ao atualizar sessão RLS:', sessionError)
          throw new Error('Erro ao atualizar permissões de acesso')
        }

        console.log('✅ [ChangeUser] Sessão RLS atualizada com sucesso')

        // Atualizar localStorage
        setMessage('Atualizando sessão local...')

        const sessionData = localStorage.getItem('rolt_user_session')
        if (!sessionData) {
          throw new Error('Sessão não encontrada')
        }

        const session = JSON.parse(sessionData)
        const updatedUser = {
          ...session.user,
          account_id: newAccount.id,
          account: newAccount
        }

        const updatedSession = {
          ...session,
          user: updatedUser
        }

        localStorage.setItem('rolt_user_session', JSON.stringify(updatedSession))

        console.log('✅ [ChangeUser] Sessão local atualizada')

        setStatus('success')
        setMessage('Conta alterada com sucesso!')

        // Aguardar um momento e redirecionar
        setTimeout(() => {
          window.location.href = '/'
        }, 1000)

      } catch (error: any) {
        console.error('❌ [ChangeUser] Erro ao trocar conta:', error)
        setStatus('error')
        setMessage(error.message || 'Erro ao trocar de conta')
        setTimeout(() => navigate('/'), 3000)
      }
    }

    changeAccount()
  }, [searchParams, user, navigate])

  return (
    <div className="min-h-screen w-full bg-neutral-900 flex items-center justify-center">
      <div className="flex flex-col items-center gap-6 max-w-md w-full px-6">
        {/* Logo */}
        <img
          src="/logo-white.png"
          alt="Logo"
          className="w-32 h-auto mb-4"
        />

        {/* Loading Spinner */}
        {status === 'loading' && (
          <div className="relative">
            <div className="w-16 h-16 border-4 border-neutral-700 border-t-red-500 rounded-full animate-spin" />
          </div>
        )}

        {/* Success Icon */}
        {status === 'success' && (
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        )}

        {/* Error Icon */}
        {status === 'error' && (
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
        )}

        {/* Message */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-2">
            {status === 'loading' && 'Trocando Conta'}
            {status === 'success' && 'Sucesso!'}
            {status === 'error' && 'Erro'}
          </h2>
          <p className="text-neutral-400">{message}</p>
        </div>

        {/* Progress Indicator */}
        {status === 'loading' && (
          <div className="w-full max-w-xs">
            <div className="h-1 bg-neutral-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-red-500 to-red-600 animate-pulse"
                   style={{ width: '60%' }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ChangeUser
